import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import { useAuth } from './AuthContext'
import { apiClient } from '@/services/api/client'

// User profile type
export interface UserProfile {
  email: string
  name: string
  role: 'SuperUser' | 'Admin' | 'User'
  permissions: string[]
  is_active: boolean
}

// Context type
interface UserContextType {
  user: UserProfile | null
  hasAccess: boolean
  isLoading: boolean
  refetchUser: () => Promise<void>
}

const UserContext = createContext<UserContextType | undefined>(undefined)

// User Provider
export function UserProvider({ children }: { children: ReactNode }) {
  const { getAccessToken, isAuthenticated } = useAuth()
  const [user, setUser] = useState<UserProfile | null>(null)
  const [hasAccess, setHasAccess] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  const fetchUserProfile = async (retryCount = 0) => {
    try {
      const token = await getAccessToken()

      if (!token) {
        // Retry once after delay if we have session data
        if (retryCount === 0 && sessionStorage.length > 0) {
          await new Promise((resolve) => setTimeout(resolve, 1000))
          return fetchUserProfile(1)
        }
        setHasAccess(false)
        setIsLoading(false)
        return
      }

      const response = await apiClient.get('/v1/users/me', {
        headers: { Authorization: `Bearer ${token}` },
      })

      if (response.data.has_access) {
        setUser(response.data.user)
        setHasAccess(true)
      } else {
        setHasAccess(false)
      }
    } catch (error) {
      console.error('Failed to fetch user profile:', error)
      // Retry once on failure
      if (retryCount === 0) {
        await new Promise((resolve) => setTimeout(resolve, 500))
        return fetchUserProfile(1)
      }
      setHasAccess(false)
    } finally {
      if (retryCount > 0 || !sessionStorage.length) {
        setIsLoading(false)
      }
    }
  }

  useEffect(() => {
    if (isAuthenticated) {
      fetchUserProfile()
    } else {
      setIsLoading(false)
    }
  }, [isAuthenticated])

  return (
    <UserContext.Provider
      value={{
        user,
        hasAccess,
        isLoading,
        refetchUser: fetchUserProfile,
      }}
    >
      {children}
    </UserContext.Provider>
  )
}

// Hook to use user context
export function useUser() {
  const context = useContext(UserContext)
  if (!context) {
    throw new Error('useUser must be used within UserProvider')
  }
  return context
}
